import { LitElement, html, nothing } from 'lit';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { style } from './login.style.js';
import { DebugService } from '../../../services/debug/debug.service.js';
import SsoService from '../../../services/sso/sso.service.js';
import './offer-banner.component.js';
import { CustomerOffersService } from '../../../services/customer-offers/customer-offers.service.js';
import { stratum } from '../../../constants/stratum.js';
import { iconBranchAsset } from './assets/icon-branch.js';
import { iconNewUserAsset } from './assets/icon-new-user.js';
import { CURRENT_STEP, REFI_WEB_UI_CONST, RETURN_CUST_COOKIE_CONST } from '../../../constants/constants.js';
import { QUERY_PARAMS } from '../../../constants/login.constants.js';
import SessionService from '../../../services/session/session.service.js';
import '@cof/fs-circular-loader-wc/fs-circular-loader.js';
import '@cof/fs-alert-wc/fs-alert.js';
import { StorageService } from '../../../services/storage/storage.service.js';
import { CookieService } from '../../../services/cookie/cookie.service.js';
import { CodelessEntryRoutingService, getCodelessEntryV1Flow, clearCodelessEntryV1Flow } from '../../../services/codeless-entry/codeless-entry.service.js';
import ReturnCustValidationService from '../../../services/return-cust-validation/return-cust-validation.service.js';
import ExpressReturnRoutingService from '../../../services/express-return/express-return-routing.service.js';
import { redirectToLanding } from '../../../utils/redirect-to-landing.js';
import { HostAppEventsService } from '../../../utils/host-app-events-service.js';

class RefiLogin extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('LoginComponent');
    this.loggedIn = false;
    this.loading = false;
    this.checkReturnExperienceId();
    // this.fetchUserInfo(); //moving to connectedCallback for testing async functionality
    this.backButtonRefreshPage = this.backButtonRefreshPage.bind(this);
  }

  static get properties() {
    return {
      loggedIn: { type: Boolean, reflect: true },
      loading: { type: Boolean, reflect: true },
      offer: { type: Object },
      featureToggles: { type: Object, attribute: 'feature-toggles' },
    };
  }

  static get styles() {
    return [style];
  }

  checkReturnExperienceId() {
    const urlParams = new URLSearchParams(window.location.search);
    const returnId = urlParams.get('returnState') || urlParams.get('walletState') || null;
    if (returnId) {
      sessionStorage.setItem('returnIdExperience', returnId);
    }
  }

  async fetchUserInfo() {
    this.loading = true;
    const urlParams = new URLSearchParams(window.location.search);
    const stsToken = urlParams.get(REFI_WEB_UI_CONST.stsParam);
    if (stsToken) {
      await this.handleStsToken(stsToken, urlParams);
    } else if (this.isCwMarketingEntry(urlParams)) {
      this.handleCwMarketingEntry();
    } else if (urlParams.get('prompt') && urlParams.get('noRedirect')) {
      // redirected for non-fin
      await this.goToNonSsoRouting();
    } else if (urlParams.get('noRedirect')) {
      // redirected for fin
      this.goToSsoLogin();
    } else if (urlParams.get(QUERY_PARAMS.REFI_ALLOCATION)) {
      this.refiAllocationLoginGuard(urlParams);
    } else if (urlParams.get(QUERY_PARAMS.REFI_ANALYTICS)) {
      this.refiAnalyticsLoginGuard(urlParams);
    } else if (urlParams.get('returnCust')) {
      this.returnCustLogin();
    } else if (this.shouldAutoResumeReturnCustLogin()) {
      // Customer has completed SSO login before on this browser; skip the sign-in
      // card choice and take them straight to SSO.
      this.goToSsoLogin();
    } else if (window?.location?.pathname?.includes('/login')) {
      // not redirected to get here
      const [, , profileRefId] = document?.cookie?.match('(^|;) ?C1_PRID=([^;]*)(;|$)') || ['', '', ''];
      const attemptedStsBypass = SessionService.getAttemptedStsBypass() === 'true';
      const stsBypassed = StorageService.getStsBypass() === 'true';

      if ((profileRefId || stsBypassed) && window.location.pathname.includes('/login') && !urlParams.get('error')) {
        await this.handleBypassSuccess();
      } else if (attemptedStsBypass && urlParams.get('error')) {
        await this.handleStsBypassError();
      } else {
        stratum.publishTag(TagKey.LOGIN_SSO_BYPASS_FAILURE);

        // host-app-ui redirected back without an error in query param, might have caused by /auth failure. This should handle with/wihtout STS bypass scenario
        if (getCodelessEntryV1Flow() === 'true') {
          this.routeCodelessEntryToOfferPage();
        }

        this.loading = false;
      }
    }
  }

  async handleStsToken(stsToken, urlParams) {
    // EASE GR/BAU Checks
    const externalId = SessionService.getExternalId();
    if (externalId) {
      if (externalId.startsWith(REFI_WEB_UI_CONST.EASE_GR_PREFIX) || externalId.startsWith(REFI_WEB_UI_CONST.EASE_HELM_PREFIX)) {
        SessionService.setEaseGrStsRoutingFlag();
      } else if (externalId.startsWith(REFI_WEB_UI_CONST.EASE_BAU_PREFIX)) {
        SessionService.setEaseBauStsRoutingFlag();
      } else if (externalId.startsWith(REFI_WEB_UI_CONST.CW_GR_PREFIX) || externalId.startsWith(REFI_WEB_UI_CONST.CW_HELM_PREFIX)) {
        SessionService.setCwOfferStsRoutingFlag();
      } else if (externalId.startsWith(REFI_WEB_UI_CONST.CW_BAU_PREFIX)) {
        SessionService.setCwBauStsRoutingFlag();
      }
    }

    // Codeless-Entry, Express Entry Checks
    if (urlParams.get(QUERY_PARAMS.REFI_ALLOCATION)) {
      this.refiAllocationLoginGuard(urlParams, false);
    } else if (urlParams.get(QUERY_PARAMS.REFI_ANALYTICS)) {
      this.refiAnalyticsLoginGuard(urlParams, false);
    }

    stratum.publishTag(TagKey.LOGIN_SSO_BYPASS_AND_STS_ATTEMPTED);
    await this.delayWithLog(1000);
    SsoService.goToStsLoginLink(stsToken);
  }

  handleBypassSuccess() {
    this.debug.log('Bypassing customer');
    trackAnalyticsEvent('customer bypassed');
    stratum.publishTag(TagKey.LOG_IN_SSO_BYPASS_SUCCESSFUL);
    this.goToSsoLogin();
  }

  async handleStsBypassError() {
    stratum.publishTag(TagKey.LOGIN_SSO_BYPASS_AND_STS_FAILED);
    SessionService.deleteAttemptedStsBypass();
    if (SessionService.getEaseGrStsRoutingFlag()) {
      SessionService.removeEaseGrStsRoutingFlag();
      const externalId = SessionService.getExternalId();
      redirectToLanding(REFI_WEB_UI_CONST.EASE_GR_LANDING_PAGE, QUERY_PARAMS.EXTERNAL_ID, externalId);
    } else if (SessionService.getEaseBauStsRoutingFlag()) {
      SessionService.removeEaseBauStsRoutingFlag();
      const externalId = SessionService.getExternalId();
      redirectToLanding(REFI_WEB_UI_CONST.EASE_BAU_LANDING_PAGE, QUERY_PARAMS.EXTERNAL_ID, externalId);
    } else if (SessionService.getCwOfferStsRoutingFlag()) {
      SessionService.removeCwOfferStsRoutingFlag();
      const externalId = SessionService.getExternalId();
      redirectToLanding(REFI_WEB_UI_CONST.CW_OFFER_LANDING_PAGE, QUERY_PARAMS.EXTERNAL_ID, externalId);
    } else if (SessionService.getCwBauStsRoutingFlag()) {
      SessionService.removeCwBauStsRoutingFlag();
      const externalId = SessionService.getExternalId();
      redirectToLanding(REFI_WEB_UI_CONST.CW_BAU_LANDING_PAGE, QUERY_PARAMS.EXTERNAL_ID, externalId);
    } else if (getCodelessEntryV1Flow() === 'true') {
      // Codeless-entry customers can't be identified via the login cards, so send them to the access-code page instead.
      this.routeCodelessEntryToOfferPage();
    } else {
      this.loading = false;
    }
  }

  routeCodelessEntryToOfferPage() {
    clearCodelessEntryV1Flow();
    HostAppEventsService.sendNextStepEvent({ step: CURRENT_STEP.OFFER });
  }

  isCwMarketingEntry(urlParams) {
    if (urlParams.get('source') !== REFI_WEB_UI_CONST.refiMarketingParam) return false;
    const externalId = SessionService.getExternalId();
    return !!(externalId && (externalId.startsWith(REFI_WEB_UI_CONST.CW_GR_PREFIX) || externalId.startsWith(REFI_WEB_UI_CONST.CW_BAU_PREFIX) || externalId.startsWith(REFI_WEB_UI_CONST.CW_HELM_PREFIX)));
  }

  handleCwMarketingEntry() {
    const externalId = SessionService.getExternalId();
    const landingPage = externalId.startsWith(REFI_WEB_UI_CONST.CW_BAU_PREFIX) ? REFI_WEB_UI_CONST.CW_BAU_LANDING_PAGE : REFI_WEB_UI_CONST.CW_OFFER_LANDING_PAGE;
    redirectToLanding(landingPage, QUERY_PARAMS.EXTERNAL_ID, externalId);
  }

  async redirectToEaseLandingPage() {
    stratum.publishTag(TagKey.LOG_IN_REDIRECT_TO_GR_CONTROL_LANDING_PAGE_FLOW);
    const externalId = SessionService.getExternalId();
    await this.delayWithLog(1000);
    this.debug.log('Redirecting to LP after timeout');
    redirectToLanding(REFI_WEB_UI_CONST.GR_EASE_LANDING_PAGE, QUERY_PARAMS.EXTERNAL_ID, externalId);
  }

  async connectedCallback() {
    super.connectedCallback();
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_SIGN_IN);
    this.offer = CustomerOffersService.getOffer();
    this.showOfferBanner = StorageService.getShowOfferBanner() ?? true;
    await this.fetchUserInfo();
    window.addEventListener('pageshow', this.backButtonRefreshPage, false);
  }

  render() {
    return html`
      <refi-track-pageview pagename="login"></refi-track-pageview>
      ${this.loading
        ? html`<refi-loading-spinner></refi-loading-spinner>`
        : html`${!this.showOfferBanner ? nothing : this.offer ? html`<refi-offer-banner .offer="${this.offer}"></refi-offer-banner>` : nothing}
              <fs-heading size="medium" class="sign-in__banner"><span class="sign-in__light">See if you’re pre-qualified in minutes—</span>it won’t hurt your credit score.</fs-heading>
              <div class="container">
                ${this.loggedIn
                  ? html`
                      <refi-sign-in-card class="sign-in__make-it-easy" @click="${this.goToSsoLogin}">
                        <div slot="icon">${iconBranchAsset}</div>
                        <span slot="header">Make it easy for me.</span>
                        <span slot="content">Since you're signed in, click here to pre-fill your information.</span>
                      </refi-sign-in-card>
                    `
                  : html` <refi-sign-in-card class="sign-in__returning-customer" @click="${this.goToSsoLogin}">
                      <div slot="icon">${iconBranchAsset}</div>
                      <span slot="header">I have a Capital One log-in</span>
                      <span slot="content">Use your login to pre-fill your pre-qualification request.</span>
                    </refi-sign-in-card>`}

                <refi-sign-in-card class="sign-in__new-customer" image="assets/icon-new-user.svg" @click="${this.goToNonSsoRouting}">
                  <div slot="icon">${iconNewUserAsset}</div>
                  <span slot="header">I do not have a Capital One log-in</span>
                  <span slot="content">Create a login to start your pre-qualification request.</span>
                </refi-sign-in-card>
              </div>`}
    `;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.backButtonRefreshPage, false);
  }

  backButtonRefreshPage(evt) {
    if (evt.persisted) {
      setTimeout(() => {
        window.location.reload();
      }, 1);
    }
  }

  async delayWithLog(milliseconds) {
    await new Promise(resolve => {
      setTimeout(() => {
        this.debug.log('Timeout complete');
        resolve();
      }, milliseconds);
    });
  }

  async goToNonSsoRouting() {
    trackAnalyticsEvent(`click non-sso`);
    stratum.publishTag(TagKey.LOG_IN_NON_FINANCIAL_LOGIN);
    SsoService.goToSSORegistrationLink();
  }

  goToSsoLogin() {
    trackAnalyticsEvent(`clicked sso`);
    stratum.publishTag(TagKey.LOG_IN_FINANCIAL_LOGIN);
    SsoService.goToSsoLoginLink();
  }

  returnCustLogin() {
    SsoService.goToSsoLoginLink();
  }

  shouldAutoResumeReturnCustLogin() {
    return this.isFeatureEnabled(RETURN_CUST_COOKIE_CONST.FEATURE_TOGGLE) && !!CookieService.getCookie(REFI_WEB_UI_CONST.RETURN_CUST_COOKIE);
  }

  refiAnalyticsLoginGuard(urlParams, routeToSsoLogin = true) {
    const refiAnalyticsParamValue = urlParams.get(QUERY_PARAMS.REFI_ANALYTICS);
    this.debug.log('refiAnalyticsLoginGuard: Using refi_analytics parameter for express return', refiAnalyticsParamValue);

    // Validate and parse the refi_analytics parameter using the validation service
    const parsedRefiAnalytics = ReturnCustValidationService.validateAndParseReturnCust(refiAnalyticsParamValue);

    if (!parsedRefiAnalytics) {
      this.debug.log('refiAnalyticsLoginGuard: not in expected format, using default behavior: sending user to SSO login', refiAnalyticsParamValue);
      if (routeToSsoLogin) this.returnCustLogin();
      return;
    }

    this.debug.log('refiAnalyticsLoginGuard: parsed refi_analytics data', parsedRefiAnalytics);

    // Handle express return routing for refi_analytics
    ExpressReturnRoutingService.handleExpressReturnRouting(parsedRefiAnalytics, refiAnalyticsParamValue, this.isFeatureEnabled.bind(this));

    if (routeToSsoLogin) this.returnCustLogin();
  }

  refiAllocationLoginGuard(urlParams, routeToSsoLogin = true) {
    const refiAllocationParamValue = urlParams.get(QUERY_PARAMS.REFI_ALLOCATION);
    this.debug.log('refiAllocationLoginGuard: Using refi_allocation parameter for codeless entry', refiAllocationParamValue);

    stratum.publishTag(TagKey.CODELESS_ENTRY_FOUND_REFI_ALLOCATION, {
      replacements: { value: refiAllocationParamValue },
    });

    // Validate and parse the refi_allocation parameter using the validation service
    const parsedRefiAllocation = ReturnCustValidationService.validateAndParseReturnCust(refiAllocationParamValue);

    if (!parsedRefiAllocation) {
      const failureReason = ReturnCustValidationService.getCodelessEntryFailureReason(refiAllocationParamValue);
      this.debug.log('refiAllocationLoginGuard: not in expected format, using default behavior: sending user to SSO login', refiAllocationParamValue, failureReason);
      stratum.publishTag(TagKey.CODELESS_ENTRY_NOT_ELIGIBLE, {
        replacements: { value: JSON.stringify({ refiAllocationParamValue, failureReason, message: 'refiAllocationLoginGuard: not in expected format' }) },
      });
      if (routeToSsoLogin) this.returnCustLogin();
      return;
    }

    this.debug.log('refiAllocationLoginGuard: parsed refi_allocation data', parsedRefiAllocation);

    this.debug.log('refiAllocationLoginGuard: calling CodelessEntryRoutingService with:', {
      isCodelessEntryV1: parsedRefiAllocation.isCodelessEntryV1,
      allocation: parsedRefiAllocation.allocation,
    });

    // Handle codeless entry routing for refi_allocation
    CodelessEntryRoutingService.handleCodelessEntryRouting(parsedRefiAllocation, this.isFeatureEnabled.bind(this));

    if (routeToSsoLogin) this.returnCustLogin();
  }

  isFeatureEnabled(featureKey) {
    if (!this.featureToggles) return false;
    const toggle = this.featureToggles.find(feature => feature.featureKey === featureKey);
    return toggle ? toggle.isEnabled : false;
  }

}

customElements.define('refi-login', RefiLogin);
